import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Select, Drawer, Spin } from "antd";
import {
  PRIORITY_OPTIONS,
  REPEAT_OPTIONS,
  RELATED_TO_OPTIONS,
  TAG_OPTIONS,
  fetchStaffDropdown,
  fetchRelatedOptions,
  fetchTaskById,
  updateTask,
  buildTaskPayload,
  toDateInputValue,
} from "./taskApi";
import Swal from "sweetalert2";

const emptyForm = {
  name: "",
  description: "",
  priority: undefined,
  startdate: "",
  duedate: "",
  repeat_every: undefined,
  rel_id: undefined,
  rel_type: undefined,
  is_public: false,
  billable: false,
  hourly_rate: "",
  status: 1,
  assignments: [],
  followers: [],
  tags: [],
};

const FETCHABLE_REL_TYPES = [
  "lead",
  "customer",
  "project",
  "estimate",
  "invoice",
  "proposal",
];

const DrawerResponsiveStyles = () => (
  <style>
    {`
    @media (max-width: 576px) {
      .task-detail-drawer .ant-drawer-content-wrapper { width: 90% !important; }
    }
    @media (min-width: 577px) and (max-width: 992px) {
      .task-detail-drawer .ant-drawer-content-wrapper { width: 60% !important; }
    }
    @media (min-width: 993px) and (max-width: 1440px) {
      .task-detail-drawer .ant-drawer-content-wrapper { width: 45% !important; }
    }
    @media (min-width: 1441px) {
      .task-detail-drawer .ant-drawer-content-wrapper { width: 23% !important; }
    }
    `}
  </style>
);

// mode: "edit" | "view" — same fields either way, "view" just disables everything
// taskId: id of the task to load (from GetTaskByIdnew)
// open / onClose: drawer visibility
// onSuccess: called after a successful update (edit mode only) — parent should close + refetch
const TaskDetailDrawer = ({
  mode,
  taskId,
  open,
  onClose,
  onSuccess,
  type,
  id,
}) => {
  const isView = mode === "view";

  const [form, setForm] = useState(emptyForm);

  console.log(form, "formmmmmmmmm");
  const [staffOptions, setStaffOptions] = useState([]);
  const [relatedOptions, setRelatedOptions] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [loadingTask, setLoadingTask] = useState(false);
  const [saving, setSaving] = useState(false);

  const isRelatedLocked = Boolean(type && id);

  useEffect(() => {
    fetchStaffDropdown()
      .then(setStaffOptions)
      .catch((err) => console.error("Failed to load staff dropdown", err));
  }, []);

  const parseStaffIds = (value) => {
    if (!value) return [];

    return String(value)
      .split(",")
      .map((item) => Number(item.split(":")[0].trim()))
      .filter((id) => !Number.isNaN(id));
  };
  // Load the task whenever the drawer is opened for a given taskId
  useEffect(() => {
    if (!open || !taskId) return;

    setLoadingTask(true);
    fetchTaskById(taskId)
      .then((task) => {
        if (!task) return;
        setForm({
          name: task.name || "",
          description: task.description || "",
          priority: task.priority ?? undefined,
          startdate: toDateInputValue(task.startdate),
          duedate: toDateInputValue(task.duedate),
          repeat_every: task.repeat_every ?? 0,

          // Lead/Customer context gets priority
          rel_id: type && id ? Number(id) : (task.rel_id ?? undefined),
          rel_type: type || task.rel_type || undefined,

          is_public: !!task.is_public,
          billable: !!task.billable,
          hourly_rate: task.hourly_rate || "",
          status: task.status || 1,

          assignments: parseStaffIds(task.assigned_staff_ids_and_names),
          followers: parseStaffIds(task.follower_staff_ids_and_names),
          tags: [],
        });
      })
      .catch((err) => console.error("Failed to load task", err))
      .finally(() => setLoadingTask(false));
  }, [open, taskId]);

  // Fetch the related list whenever rel_type is set (including on initial load)
  useEffect(() => {
    if (!form.rel_type || !FETCHABLE_REL_TYPES.includes(form.rel_type)) {
      setRelatedOptions([]);
      return;
    }
    setLoadingRelated(true);
    fetchRelatedOptions(form.rel_type)
      .then(setRelatedOptions)
      .catch((err) => console.error("Failed to load related options", err))
      .finally(() => setLoadingRelated(false));
  }, [form.rel_type]);

  const updateField = (field, value) => {
    if (isView) return;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRelTypeChange = (value) => {
    if (isView) return;
    setForm((prev) => ({ ...prev, rel_type: value, rel_id: undefined }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = buildTaskPayload(form);
      const result = await updateTask(taskId, payload);
      if (result?.Status) {
        Swal.fire({
          icon: "success",
          title: "Task Updated Successfully",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
        });
        onSuccess?.(result);
      } else {
        console.error("Update task failed", result?.Message);
      }
    } catch (err) {
      console.error("Failed to update task", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DrawerResponsiveStyles />
      {saving && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <Drawer
        title={isView ? "View Task" : "Edit Task"}
        placement="right"
        open={open}
        onClose={onClose}
        rootClassName="task-detail-drawer"
        destroyOnClose
        closable={false}
        styles={{
          body: {
            paddingTop: 1,
          },
        }}
        extra={
          <div className="d-flex align-items-center gap-2">
            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm"
              style={{
                width: "35px",
                height: "35px",
                backgroundColor: "#e40d22",
                color: "#fff",
                border: "none",
              }}
              title="Close"
            >
              <i className="fa fa-times" />
            </button>

            {/* Save - only in Edit mode */}
            {!isView && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor: "#07f083",
                  color: "#fff",
                  border: "none",
                }}
                title="Save"
              >
                {saving ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                ) : (
                  <i className="fa fa-check" />
                )}
              </button>
            )}
          </div>
        }
      >
        <Spin spinning={loadingTask}>
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="content pt-0">
              <div className="d-flex mb-2 row justify-content-between align-items-center">
                <div className="col-6 d-none">
                  <label className="form-label">Attachments:</label>
                  <input
                    type="file"
                    className="form-control"
                    disabled={isView}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.name}
                    disabled={isView}
                    readOnly={isView}
                    onChange={(e) => updateField("name", e.target.value)}
                  />
                </div>

                <div className="col-4 mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.startdate}
                    disabled={isView}
                    readOnly={isView}
                    onChange={(e) => updateField("startdate", e.target.value)}
                  />
                </div>
                <div className="col-4 mb-3">
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.duedate}
                    disabled={isView}
                    readOnly={isView}
                    onChange={(e) => updateField("duedate", e.target.value)}
                  />
                </div>

                {form.billable && (
                  <div className="col-4 mb-3">
                    <label className="form-label">Hourly Rate</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={form.hourly_rate}
                      disabled={isView}
                      readOnly={isView}
                      onChange={(e) =>
                        updateField("hourly_rate", e.target.value)
                      }
                    />
                  </div>
                )}

                <div className="col-6 mb-3 d-none">
                  <label className="form-label">Repeat every</label>
                  <Select
                    className="w-100"
                    style={{ height: 38 }}
                    placeholder="Repeat Every"
                    value={form.repeat_every}
                    disabled={isView}
                    onChange={(value) => updateField("repeat_every", value)}
                    options={REPEAT_OPTIONS}
                  />
                </div>

                <div className="col-6 mb-3">
                  <label className="form-label">Related To </label>
                  <Select
                    className="w-100"
                    style={{ height: 38 }}
                    placeholder="Related To"
                    value={form.rel_type}
                    // disabled={isView}
                    disabled={isRelatedLocked}
                    onChange={handleRelTypeChange}
                    options={RELATED_TO_OPTIONS}
                  />
                </div>

                {form.rel_type && (
                  <div className="col-6 mb-3">
                    <label className="form-label">
                      {RELATED_TO_OPTIONS.find((o) => o.value === form.rel_type)
                        ?.label || "Related Item"}
                    </label>
                    <Select
                      className="w-100"
                      style={{ height: 38 }}
                      placeholder={`Select ${form.rel_type}`}
                      value={form.rel_id}
                      // disabled={isView}
                      disabled={isRelatedLocked}
                      onChange={(value) => updateField("rel_id", value)}
                      loading={loadingRelated}
                      notFoundContent={
                        FETCHABLE_REL_TYPES.includes(form.rel_type)
                          ? undefined
                          : "No list wired up yet"
                      }
                      options={relatedOptions}
                    />
                  </div>
                )}

                <div className="col-6 mb-3">
                  <label className="form-label">Priority </label>
                  <Select
                    className="w-100"
                    style={{ height: 38 }}
                    placeholder="Select Priority"
                    value={form.priority}
                    disabled={isView}
                    onChange={(value) => updateField("priority", value)}
                    options={PRIORITY_OPTIONS}
                  />
                </div>

                <div className="col-6 mt-5">
                  <label className="me-4">
                    <input
                      type="checkbox"
                      name="public"
                      checked={form.is_public}
                      disabled={isView}
                      onChange={(e) =>
                        updateField("is_public", e.target.checked)
                      }
                    />{" "}
                    Public
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      name="billable"
                      checked={form.billable}
                      disabled={isView}
                      onChange={(e) =>
                        updateField("billable", e.target.checked)
                      }
                    />{" "}
                    Billable
                  </label>
                </div>

                <div className="col-12 mb-3">
                  <label className="form-label">Assignees </label>
                  <Select
                    className="w-100"
                    mode="multiple"
                    style={{ height: 38 }}
                    placeholder="Assign To"
                    value={form.assignments}
                    disabled={isView}
                    onChange={(value) => updateField("assignments", value)}
                    options={staffOptions}
                  />
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label">Followers </label>
                  <Select
                    className="w-100"
                    style={{ height: 38 }}
                    mode="multiple"
                    placeholder="Followers"
                    value={form.followers}
                    disabled={isView}
                    onChange={(value) => updateField("followers", value)}
                    options={staffOptions}
                  />
                </div>
              </div>

              <div className="col-12 mb-2 d-none">
                <label className="form-label">Tags:</label>
                <Select
                  className="w-100"
                  style={{ height: 38 }}
                  mode="multiple"
                  placeholder="tags"
                  value={form.tags}
                  disabled={isView}
                  onChange={(value) => updateField("tags", value)}
                  options={TAG_OPTIONS}
                />
              </div>

              <div>
                <label className="form-label">Task Description:</label>
                <textarea
                  rows={3}
                  className="form-control"
                  placeholder="Add Description"
                  value={form.description}
                  disabled={isView}
                  readOnly={isView}
                  onChange={(e) => updateField("description", e.target.value)}
                />
              </div>

              <div className="d-flex justify-content-end d-none">
                <button
                  type="button"
                  className="btn btn-secondary d-flex mt-3 me-2"
                  onClick={onClose}
                >
                  {isView ? "Close" : "Cancel"}
                </button>
                {!isView && (
                  <button
                    type="button"
                    className="btn btn-primary d-flex mt-3"
                    disabled={saving}
                    onClick={handleSubmit}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </Spin>
      </Drawer>
    </>
  );
};

TaskDetailDrawer.propTypes = {
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  type: PropTypes.oneOf([
    "lead",
    "customer",
    "project",
    "estimate",
    "invoice",
    "proposal",
  ]),
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default TaskDetailDrawer;
